import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Hammer, Shield, Anchor, Flame, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

const HERO = "https://static.prod-images.emergentagent.com/jobs/58fc8f05-e99b-4946-b4cb-e7f6df7eb0e5/images/17f56cc1783288e9e899cd71e175de00652322b80183799471a7e0e74524072e.jpeg";

const STEPS = [
    { icon: Anchor, t: "Iron in the four corners", d: "Railroad-iron spikes dressed with sulfur, red ochre, and scorched gravel are driven into each corner of the land and spoken over as silent sentries." },
    { icon: Shield, t: "Thresholds & sills sealed", d: "Doorways and window sills are locked so betrayal, wandering sickness, and envious eyes stop dead at the gravel path." },
    { icon: Hammer, t: "Return the malice", d: "Any foot-track pin or hot dust laid at the gate is broken and turned back on the hand that set it." },
    { icon: Flame, t: "The ground made sovereign", d: "Once the corners are pinned and the salt is on the sill, the household, home, farm, or business, sleeps under absolute spiritual cover." },
];

export default function LandDefense() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", property_type: "Home", acreage: "", concern: "" });
    const [busy, setBusy] = useState(false);
    const [packages, setPackages] = useState([]);

    useEffect(() => {
        api.get("/services").then((r) => setPackages(r.data.filter((s) => s.category === "Land Defense").sort((a, b) => a.price - b.price)));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/land-defense", form);
            toast.success("Intake received. The house will reach out to walk your ground.");
            setForm({ name: "", email: "", phone: "", address: "", property_type: "Home", acreage: "", concern: "" });
        } catch { toast.error("Could not send intake."); }
        setBusy(false);
    };

    const input = "w-full bg-surface border border-gold/20 rounded-xl px-4 py-3 text-cream placeholder:text-faded focus:outline-none focus:ring-1 focus:ring-gold text-sm";

    return (
        <div>
            <section className="relative h-[52vh] flex items-end">
                <img src={HERO} alt="Land defense" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
                <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-12 w-full">
                    <Overline>The Four Corners</Overline>
                    <h1 className="mt-3 font-serif text-5xl sm:text-7xl text-cream leading-[0.95]">Land Defense &<br />Property Pinning</h1>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-14">
                <Reveal>
                    <Overline>What This Work Is</Overline>
                    <h2 className="mt-3 font-serif text-4xl text-cream">A house is only as safe as its fence line.</h2>
                    <p className="mt-5 text-ash leading-relaxed">Before cameras and codes, there was iron in the dirt. We guard the ground itself, private estates, homes, farmland, and commercial enterprises, with the old craft of forged iron, sulfur, and unyielding ancestral law.</p>
                    <p className="mt-4 text-ash leading-relaxed">Once the corners are pinned and the sills are salted, what walks against your household walks into the iron first. Domestic sabotage loses its grip, and the petty tricks dropped at your gate turn back on the hand that laid them.</p>
                    <ul className="mt-6 space-y-3">
                        {["Consecration of the four corners with iron & sulfur", "Threshold and sill sealing against betrayal", "Return-to-sender on works laid at the gate", "Full perimeter consecration for home, farm, or business"].map((x) => (
                            <li key={x} className="flex gap-3 text-sm text-ash"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />{x}</li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal delay={0.1}>
                    <form onSubmit={submit} className="p-7 rounded-2xl bg-surface border border-gold/15" data-testid="land-defense-form">
                        <h2 className="font-serif text-2xl text-cream mb-1">Property Intake</h2>
                        <p className="text-xs text-faded mb-5">Tell the house about your ground and we'll walk it with you.</p>
                        <div className="space-y-4">
                            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="ld-name" />
                            <div className="grid grid-cols-2 gap-3">
                                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="ld-email" />
                                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} data-testid="ld-phone" />
                            </div>
                            <input placeholder="Property address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={input} data-testid="ld-address" />
                            <div className="grid grid-cols-2 gap-3">
                                <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={input} data-testid="ld-type">
                                    <option>Home</option><option>Farmland</option><option>Estate</option><option>Business</option><option>Other</option>
                                </select>
                                <input placeholder="Acreage / size" value={form.acreage} onChange={(e) => setForm({ ...form, acreage: e.target.value })} className={input} data-testid="ld-acreage" />
                            </div>
                            <textarea required rows={4} placeholder="What's troubling the ground? Describe the concern…" value={form.concern} onChange={(e) => setForm({ ...form, concern: e.target.value })} className={input} data-testid="ld-concern" />
                        </div>
                        <button disabled={busy} data-testid="ld-submit" className="mt-6 w-full bg-gold text-ink rounded-full py-3 font-medium hover:bg-goldbright transition-colors active:scale-95 disabled:opacity-60">
                            {busy ? "Sending…" : "Request Land Defense"}
                        </button>
                    </form>
                </Reveal>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-4">
                <Reveal><div className="mb-10 text-center"><Overline>Defense Packages</Overline><h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream">Pin your ground</h2><p className="mt-3 text-ash max-w-2xl mx-auto">Set, priced packages for a full four-corner working. Book and pay securely, and the house will schedule the walk.</p></div></Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {packages.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.08}>
                            <div data-testid={`ld-package-${p.id}`} className="h-full flex flex-col rounded-2xl overflow-hidden bg-surface border border-gold/10 hover:border-gold/40 transition-colors">
                                <img src={p.image} alt={p.name} className="w-full h-40 object-cover" />
                                <div className="p-6 flex flex-col flex-1">
                                    <div className="text-[10px] uppercase tracking-widest text-faded">{p.duration}</div>
                                    <h3 className="mt-1 font-serif text-2xl text-cream">{p.name}</h3>
                                    <p className="mt-2 text-sm text-ash flex-1">{p.description}</p>
                                    <div className="mt-4 flex items-center justify-between">
                                        <span className="text-2xl text-gold">${p.price.toFixed(0)}</span>
                                        <Link to={`/services/${p.id}`} className="inline-flex items-center gap-1 text-sm text-gold border border-gold/30 rounded-full px-4 py-2 hover:bg-gold/10 transition-colors">Book <ArrowRight className="w-4 h-4" /></Link>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-surface/40 border-y border-gold/10 py-20 mt-16">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal><div className="mb-12"><Overline>How the Ground Is Guarded</Overline><h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream">Iron, salt, and old law</h2></div></Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map((s, i) => (
                            <Reveal key={i} delay={i * 0.06}>
                                <div className="h-full p-7 rounded-2xl bg-surface border border-gold/10">
                                    <s.icon className="w-7 h-7 text-gold mb-4" />
                                    <h3 className="font-serif text-xl text-cream">{s.t}</h3>
                                    <p className="mt-2 text-sm text-ash leading-relaxed">{s.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
